import { forwardRef, useCallback } from 'react';
import FreeTextInput from '../FreeTextInput/FreeTextInput.component';
import type FreeTextInputProps from '../FreeTextInput/FreeTextInput.types';
import AddressSearchFieldSuggestions from './AddressSearchFieldSuggestions.component';
import { useAddressSearchFieldAutocomplete } from './AddressSearchField.context';

/**
 * Text field that consumes Places autocomplete from
 * `AddressSearchFieldAutocompleteProvider`.
 *
 * The provider must be mounted by a **parent** that calls
 * `useGoogleAddressAutocomplete` once and passes handlers/state into context.
 */
const AddressSearchFieldExpandedInput = forwardRef<
  HTMLInputElement,
  FreeTextInputProps
>((props, ref) => {
  const {
    onChange: onChangeFromProps,
    onKeyDown: onKeyDownFromProps,
    onBlur: onBlurFromProps,
    ...rest
  } = props;

  const {
    listboxId,
    suggestions,
    suggestionsOpen,
    activeIndex,
    getOptionId,
    handleInputChange,
    handleKeyDown,
    handleBlur,
    pickSuggestion,
  } = useAddressSearchFieldAutocomplete();

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeFromProps?.(e as never);
      handleInputChange(e.target.value);
    },
    [handleInputChange, onChangeFromProps],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onKeyDownFromProps?.(e as never);
      handleKeyDown(e as React.KeyboardEvent);
    },
    [handleKeyDown, onKeyDownFromProps],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onBlurFromProps?.(e as never);
      handleBlur();
    },
    [handleBlur, onBlurFromProps],
  );

  return (
    <div className="relative w-full">
      <FreeTextInput
        {...rest}
        ref={ref as FreeTextInputProps['ref']}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={suggestionsOpen}
        aria-controls={suggestionsOpen ? listboxId : undefined}
        aria-activedescendant={
          suggestionsOpen && activeIndex >= 0
            ? getOptionId(activeIndex)
            : undefined
        }
        onChange={onChange as FreeTextInputProps['onChange']}
        onKeyDown={onKeyDown as FreeTextInputProps['onKeyDown']}
        onBlur={onBlur as FreeTextInputProps['onBlur']}
      />
      {suggestionsOpen && (
        <AddressSearchFieldSuggestions
          listboxId={listboxId}
          suggestions={suggestions}
          activeIndex={activeIndex}
          onPick={pickSuggestion}
        />
      )}
    </div>
  );
});

AddressSearchFieldExpandedInput.displayName = 'AddressSearchFieldExpandedInput';

export default AddressSearchFieldExpandedInput;
